<template>
  <div class="settings-sms">
    <sticky-header :title="$t('settingsSms.title')">
      <btn
        type="success"
        slot="ui"
        @click.native="save">
        {{ $t('common.ui.save') }}
      </btn>

      <btn
        url="/settings"
        slot="ui">
        {{ $t('common.ui.back') }}
      </btn>
    </sticky-header>

    <block
      :title="$t('settingsSms.smsSendingSettings')"
      :isLoading="!configLoaded"
      :isSaving="saveInProgress">
      <field slot="content">
        <label slot="label">
          {{ $t('settingsSms.cancelReservationBySms') }}
        </label>

        <switcher
          v-model="smsCanCancel"
          key="sms-can-cancel-switcher"
          slot="field" />

        <small
          slot="note"
          class="note">
          {{ $t('settingsSms.smsCancelNotice') }}
        </small>
      </field>

      <field
        v-if="smsCanCancel"
        slot="content">
        <label slot="label">
          {{ $t('settingsSms.smsTextToCancel') }}
        </label>

        <input
          v-model="smsTextToCancel"
          key="sms-text-to-cancel"
          slot="field" />

        <small
          slot="note"
          class="note">
          {{ $t('settingsSms.smsTextToCancelDesc') }}
        </small>
      </field>

      <field slot="content">
        <label slot="label">
          {{ $t('settingsSms.sendReminderBeforeReservation') }}
        </label>

        <select
          :class="{ 'small-select': true }"
          v-model="smsSendBefore.hours"
          slot="field"
          name="time-spaces-hours">
          <option :value="0">0 h</option>
          <option :value="1">1 h</option>
          <option :value="2">2 h</option>
          <option :value="3">3 h</option>
          <option :value="4">4 h</option>
          <option :value="5">5 h</option>
          <option :value="6">6 h</option>
          <option :value="7">7 h</option>
          <option :value="8">8 h</option>
          <option :value="9">9 h</option>
          <option :value="10">10 h</option>
          <option :value="11">11 h</option>
          <option :value="12">12 h</option>
          <option :value="24">24 h</option>
          <option :value="48">48 h</option>
          <option :value="72">72 h</option>
        </select>

        <select
          :class="{ 'small-select': true }"
          v-model="smsSendBefore.minutes"
          slot="field"
          name="time-spaces-minutes">
          <option :value="0">0 min</option>
          <option :value="5">5 min</option>
          <option :value="10">10 min</option>
          <option :value="15">15 min</option>
          <option :value="20">20 min</option>
          <option :value="25">25 min</option>
          <option :value="30">30 min</option>
          <option :value="35">35 min</option>
          <option :value="40">40 min</option>
          <option :value="45">45 min</option>
          <option :value="50">50 min</option>
          <option :value="55">55 min</option>
        </select>
      </field>

      <field slot="content">
        <label slot="label">
          {{ $t('settingsSms.submitSmsAbroad') }}
        </label>

        <switcher
          v-model="smsSendAbroad"
          key="send-sms-abroad-switcher"
          slot="field" />

        <small
          slot="note"
          class="note">
          {{ $t('settingsSms.smsAbroadNotice') }}
        </small>
      </field>

      <field slot="content">
        <label slot="label">
          {{ $t('settingsSms.submitSmsAtNight') }}
        </label>

        <switcher
          v-model="sendSmsAtNight"
          key="send-sms-at-night-switcher"
          slot="field" />

        <small
          slot="note"
          class="note">
          {{ $t('settingsSms.betweenNightHours') }}
        </small>
      </field>
    </block>

    <block
      :title="$t('settingsSms.smsContentTitle')"
      :isLoading="!configLoaded"
      :isSaving="saveInProgress">
      <message
        slot="content"
        type="warning">
        <strong>
          {{ $t('settingsSms.warning') }}
        </strong>

        <a href="#" @click.prevent="showSMSContentAlert">
          {{ $t('settingsSms.warningSmsContentText') }}
        </a>
      </message>

      <field slot="content">
        <label slot="label">
          {{ $t('settingsSms.smsContent') }}
        </label>

        <textarea
          v-model="smsNotification[currentLanguages.sms_notification]"
          slot="field"></textarea>

        <language-switcher
          slot="field"
          relatedTo="sms_notification" />

        <mail-sms-legend-text slot="note" />
      </field>

      <field
        v-if="hasCompanyPhoneNumber"
        slot="content">
        <label slot="label">
          {{ $t('settingsSms.submitTestSmsTo') }} <strong>{{ companyPhoneNumber }}</strong>
        </label>

        <btn
          slot="field"
          type="success with-icon"
          iconName="mailbox"
          iconColor="color-light"
          :disabled="smsInProgress || !smsCount"
          @click.native="sendSms('smsNotification', 'sms_notification')">
          {{ $t('settingsSms.submitTestSms') }}
        </btn>
      </field>

      <field
        v-if="smsCanCancel"
        slot="content">
        <label slot="label">
          {{ $t('settingsSms.smsContentForCancelledReservation') }}
        </label>

        <textarea
          v-model="smsCancel[currentLanguages.sms_cancel]"
          slot="field"></textarea>

        <language-switcher
          slot="field"
          relatedTo="sms_cancel" />

        <small
          slot="note"
          class="note">
          {{ $t('settingsSms.cancellationIsTriggeredBySmsResponse') }}
        </small>

        <mail-sms-legend-text slot="note" />
      </field>

      <field
        v-if="hasCompanyPhoneNumber && smsCanCancel"
        slot="content">
        <label slot="label">
          {{ $t('settingsSms.submitTestSmsTo') }} <strong>{{ companyPhoneNumber }}</strong>
        </label>

        <btn
          slot="field"
          type="success with-icon"
          iconName="mailbox"
          iconColor="color-light"
          :disabled="smsInProgress || !smsCount"
          @click.native="sendSms('smsCancel', 'sms_cancel')">
          {{ $t('settingsSms.submitTestSms') }}
        </btn>
      </field>

      <field
        v-if="smsCanCancel"
        slot="content">
        <label slot="label">
          {{ $t('settingsSms.smsContentForCantCancelReservation') }}
        </label>

        <textarea
          v-model="smsCantCancel[currentLanguages.sms_cant_cancel]"
          slot="field"></textarea>

        <language-switcher
          slot="field"
          relatedTo="sms_cant_cancel" />

        <small
          slot="note"
          class="note">
          {{ $t('settingsSms.cancellationIsTriggeredBySmsResponse') }}
        </small>

        <mail-sms-legend-text slot="note" />
      </field>

      <field
        v-if="hasCompanyPhoneNumber && smsCanCancel"
        slot="content">
        <label slot="label">
          {{ $t('settingsSms.submitTestSmsTo') }} <strong>{{ companyPhoneNumber }}</strong>
        </label>

        <btn
          slot="field"
          type="success with-icon"
          iconName="mailbox"
          iconColor="color-light"
          :disabled="smsInProgress || !smsCount"
          @click.native="sendSms('smsCantCancel', 'sms_cant_cancel')">
          {{ $t('settingsSms.submitTestSms') }}
        </btn>
      </field>
    </block>
  </div>
</template>

<script>
import Block from './../common/Block';
import ErrorUtils from './../helpers/ErrorUtils.js';
import Languages from './../mixins/Languages';
import MailSmsLegendText from './../common/MailSmsLegendText';
import SettingsBasicBlock from './../ui/SettingsBasicBlock';
import StickyHeader from './../common/StickyHeader';

export default {
  name: 'settings-sms',
  mixins: [
    Languages
  ],
  components: {
    'block': Block,
    'mail-sms-legend-text': MailSmsLegendText,
    'settings-basic-block': SettingsBasicBlock,
    'sticky-header': StickyHeader
  },
  data () {
    return {
      errors: [],
      smsCanCancel: false,
      sendSmsAtNight: false,
      smsSendAbroad: false,
      smsSendBefore: {
        hours: 2,
        minutes: 0
      },
      smsNotification: this.$t('settingsSms.smsNotificationTemplate'),
      smsCancel: this.$t('settingsSms.smsCancellationTemplate'),
      smsCantCancel: this.$t('settingsSms.smsCantCancelTemplate'),
      smsTextToCancel: '',
      smsInProgress: false,
      smsCount: false,
      translations: {},
      currentLanguages: {
        sms_cancel: '',
        sms_cant_cancel: '',
        sms_notification: ''
      },
      configLoaded: false,
      saveInProgress: false
    };
  },
  computed: {
    hasCompanyPhoneNumber () {
      return !!this.$store.state.config.company.phone;
    },
    companyPhoneNumber () {
      return this.$store.state.config.company.phone;
    }
  },
  mounted () {
    if (this.$store.state.config.user.id !== 0) {
      this.configurationLoad();
    }

    this.$bus.$on('configuration-loaded', this.configurationLoad);
  },
  methods: {
    configurationLoad () {
      this.loadTranslations();
      this.loadSmsCount();
      this.configureMultilanguage();
    },
    loadSmsCount () {
      this.$http.post('/panel/general/get-sms-counter').then(response => {
        this.smsCount = response.data.sms_counter;
      }).catch(error => {
        ErrorUtils.handleLoadError(error, '/panel/general/get-sms-counter', this.$router, this.$bus);
      });
    },
    loadTranslations () {
      this.$http.post('/panel/general/get-configuration-translations').then(response => {
        this.translations = response.data;

        if (!Object.keys(this.translations).length) {
          this.translations = this.createEmptyTranslationsObject([
            'sms_cancel',
            'sms_cant_cancel',
            'sms_notification'
          ]);
        }

        this.setFormData();
      }).catch(error => {
        ErrorUtils.handleLoadError(error, '/panel/general/get-configuration-translations', this.$router, this.$bus);
      });
    },
    setFormData () {
      this.configLoaded = true;
      this.smsCanCancel = this.$store.state.config.company.sms_can_cancel;
      this.sendSmsAtNight = !!this.$store.state.config.company.send_sms_at_night;
      this.smsSendAbroad = !!this.$store.state.config.company.sms_send_abroad;
      this.smsSendBefore.hours = Math.floor(this.$store.state.config.company.sms_send_before / 60);
      this.smsSendBefore.minutes = this.$store.state.config.company.sms_send_before % 60;
      this.smsTextToCancel = this.$store.state.config.company.sms_text_to_cancel;
      this.smsNotification = this.getLanguageFieldValues(
        this.translations,
        'sms_notification',
        this.$store.state.config.company.sms_notification
      );
      this.smsCancel = this.getLanguageFieldValues(
        this.translations,
        'sms_cancel',
        this.$store.state.config.company.sms_cancel
      );
      this.smsCantCancel = this.getLanguageFieldValues(
        this.translations,
        'sms_cant_cancel',
        this.$store.state.config.company.sms_cant_cancel
      );
    },
    configureMultilanguage () {
      this.currentLanguages.sms_cancel = this.defaultLanguageCode;
      this.currentLanguages.sms_cant_cancel = this.defaultLanguageCode;
      this.currentLanguages.sms_notification = this.defaultLanguageCode;
      this.$bus.$on('change-field-language', this.changeCurrentLanguageForField);
    },
    validate () {

    },
    save () {
      this.validate();

      if (this.errors.length) {
        this.$bus.$emit('notification-display', {
          message: this.$t('common.ui.invalidFields'),
          type: 'error'
        });

        return;
      } else {
        this.saveInProgress = true;
      }

      this.saveForm();
    },
    saveForm () {
      let formData = this.prepareFormData();
      formData.settings = 'sms';

      this.$http.post('/panel/settings/settings-save', formData).then(response => {
        if (response.data.success) {
          this.$bus.$emit('notification-display', {
            message: this.$t('settingsSms.smsSettingsChanged'),
            type: 'success'
          });

          this.$store.commit('config/setCompanyPartialConfig', this.prepareFormData());
          this.$router.push('/settings');
        }

        this.saveInProgress = false;
      }).catch(error => {
        ErrorUtils.handleSubmitError(error, '/panel/settings/settings-save', this.$router, this.$bus, this.saveForm.bind(this));
        this.saveInProgress = false;
      });
    },
    prepareFormData () {
      let formData = {
        sms_can_cancel: this.smsCanCancel,
        send_sms_at_night: this.sendSmsAtNight,
        sms_send_abroad: this.smsSendAbroad,
        sms_send_before: parseInt(this.smsSendBefore.hours, 10) * 60 + parseInt(this.smsSendBefore.minutes, 10),
        sms_notification: this.smsNotification[this.defaultLanguageCode],
        sms_cancel: this.smsCancel[this.defaultLanguageCode],
        sms_cant_cancel: this.smsCantCancel[this.defaultLanguageCode],
        sms_text_to_cancel: this.smsTextToCancel
      };

      if (this.languagesAreAvailable) {
        formData.translations = this.prepareTranslationsObject({
          sms_cancel: this.smsCancel,
          sms_cant_cancel: this.smsCantCancel,
          sms_notification: this.smsNotification
        });
      }

      return formData;
    },
    sendSms (type, languagePhraseField) {
      let lang = this.currentLanguages[languagePhraseField];
      let content = this[type][lang];
      content = content.replace(/\{data\}/g, '25-05-2018');
      content = content.replace(/\{godzina\}/g, '12:00');
      content = content.replace(/\{usluga\}/g, 'NAZWA USŁUGI');
      this.smsInProgress = true;

      this.$http.post('/panel/test-messages/test-sms', {
        message: content
      }).then(response => {
        if (response.data.success) {
          this.$bus.$emit('notification-display', {
            message: this.$t('settingsSms.smsSent'),
            type: 'success'
          });
        } else {
          this.$bus.$emit('notification-display', {
            message: this.$t('settingsSms.unableToSendSms'),
            type: 'error'
          });
        }

        this.smsInProgress = false;
        this.loadSmsCount();
      }).catch(error => {
        this.$bus.$emit('notification-display', {
          message: this.$t('settingsSms.unableToSendSms'),
          type: 'error'
        });
        this.smsInProgress = false;
        ErrorUtils.handleSubmitError(error, '/panel/test-messages/test-sms', this.$router, this.$bus, this.sendSms.bind(this, type, languagePhraseField));
      });
    },
    showSMSContentAlert () {
      this.$bus.$emit('alert-display', {
        title: 'Ilość wysyłanych wiadomości SMS',
        desc: `
        Stosowanie znaków specjalnych (w tym polskich znaków i znaków takich jak <strong>^{}[]~\\|&euro;</strong>) może sprawić, że wysłane zostanie więcej niż 1 wiadomość SMS w ramach pojedynczego powiadomienia klienta.<br><br>
        Najlepiej treść SMS wprowadzać z klawiatury, gdyż podczas kopiowania z dokumentów w treści SMS mogą się znaleźć białe znaki, które zawyżą rozmiar wiadomości.<br><br>
        Stosowanie wstawek, które są zamieniane np. na nazwę usługi może wpływać na długość wiadomości.<br><br>
        W wypadku stosowania znaków specjalnych 1 SMS może zawierać jedynie 70 znaków (zamiast 160 znaków w wypadku wiadomości bez znaków specjalnych).<br><br>
        Należy też pamiętać o tym, że z uwagi na charakterystykę protokołu SMS, istnieją różne limity długości dla wiadomości składających się z więcej niż jednego SMS-a:<br><br>
        <table border="1" cellpadding="2" style="border-collapse: collapse; width: 100%; font-size: 13px; text-align: center;">
          <thead>
            <tr>
              <td><strong>Liczba SMS</strong></td>
              <td><strong>Bez znaków specjalnych</strong></td>
              <td><strong>Ze znakami specjalnymi</strong></td>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>1</td>
              <td>160 znaków</td>
              <td>70 znaków</td>
            </tr>
            <tr>
              <td>2</td>
              <td>306 znaków</td>
              <td>134 znaki</td>
            </tr>
            <tr>
              <td>3</td>
              <td>459 znaków</td>
              <td>201 znaków</td>
            </tr>
            <tr>
              <td>4</td>
              <td>612 znaków</td>
              <td>268 znaków</td>
            </tr>
            <tr>
              <td>5</td>
              <td>765 znaków</td>
              <td>335 znaków</td>
            </tr>
            <tr>
              <td>6</td>
              <td>918 znaków</td>
              <td>402 znaki</td>
            </tr>
            <tr>
              <td>7</td>
              <td>1071 znaków</td>
              <td>469 znaków</td>
            </tr>
            <tr>
              <td>8</td>
              <td>1224 znaki</td>
              <td>536 znaków</td>
            </tr>
            <tr>
              <td>9</td>
              <td>1377 znaków</td>
              <td>603 znaki</td>
            </tr>
            <tr>
              <td>10</td>
              <td>1530 znaków</td>
              <td>670 znaków</td>
            </tr>
          </tbody>
        </table>`,
        allowHTML: true,
        okLabel: this.$t('settingsSms.okUnderstand'),
        isBig: true,
        okClick: () => true,
        isDanger: false
      });
    }
  },
  beforeDestroy () {
    this.$bus.$off('change-field-language', this.changeCurrentLanguageForField);
    this.$bus.$off('configuration-loaded', this.configurationLoad);
  }
};
</script>

<style lang="scss" scoped>
@import '../../assets/scss/variables.scss';

.settings-sms {
  padding: 0 0 2rem 0;
  width: 100%;

  .small-select {
    width: 10rem;

    & + .small-select {
      margin-left: 1rem;
    }
  }
}

@media (max-width: 1100px) {
  .settings-sms {
    .field {
      input,
      select,
      textarea {
        width: calc(66% - 50px);
      }
    }
  }
}

@media (max-width: 640px) {
  .settings-sms {
    margin-top: 6rem;
  }
}

@media (max-width: 580px) {
  .settings-sms {
    .field {
      label,
      input,
      select,
      textarea {
        text-align: left;
        width: calc(100% - 50px);
      }

      label {
        margin-bottom: .5rem;
      }

      small.note {
        padding: .5rem 0 0 0;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/views/SettingsSms.vue